import { useState, useEffect, useCallback } from 'react'
import GenerationControlBar from './components/GenerationControlBar'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useInfiniteMyResults } from 'hooks/query/aiStudio/useInfiniteMyResults'
import { ImageItem } from './components/ImageItem'
import { ImageData } from './utils/imageUtils'
import { ImageSlideModal } from 'components/modals/ImageSlideModal'
import useUserStore from 'store/useUserStore'

export const AIImageStudio = () => {
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  // 현재 폴링 중인 단일 요청
  const [currentPollingRequest, setCurrentPollingRequest] = useState<{
    requestId: number
    prompt: string
    totalJobs: number
    aspectRatio: string
  } | null>(null)

  const { userInfo } = useUserStore()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteMyResults({
    requestType: 'image', // Add requestType=image parameter
    enabled: !!userInfo, // Only fetch when user is logged in
  })

  // 이미지 클릭 핸들러
  const handleImageClick = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedImageUrl(null)
  }

  // 폴링 시작 시 현재 요청 설정
  const startPolling = useCallback((requestId: number, prompt: string, totalJobs: number = 4, aspectRatio: string = '9:16') => {
    setCurrentPollingRequest({ requestId, prompt, totalJobs, aspectRatio })
  }, [])

  // 폴링 완료 시 실제 데이터 새로고침
  const onPollingComplete = useCallback(() => {
    setCurrentPollingRequest(null)
    refetch() // my-results API 새로고침
  }, [refetch])

  // 실제 이미지 데이터
  const realImages: ImageData[] = data?.pages.flatMap((page: any) => 
    page.items.map((item: any) => ({
      ...item, // 새로운 API 필드들
      // 호환성을 위한 기존 필드들 매핑
      id: item.jobId,
      url: item.resultUrl,
      prompt: item.userPrompt,
      type: item.status as 'completed' | 'pending' | 'processing' | 'failed',
    }))
  ) || []

  // 폴링 중인 요청의 임시 스켈레톤 생성
  const pollingSkeletons: ImageData[] = currentPollingRequest 
    ? Array.from({ length: currentPollingRequest.totalJobs }, (_, index) => ({
        jobId: -currentPollingRequest.requestId * 1000 - index, // 음수로 임시 ID
        resultUrl: '',
        requestType: 'image' as const,
        userPrompt: currentPollingRequest.prompt,
        aspectRatio: currentPollingRequest.aspectRatio,
        imageMode: 'first_only',
        resolution: '1024x1792',
        durationSeconds: 0,
        status: 'pending' as const,
        createdAt: new Date().toISOString(),
        completedAt: '',
        requestId: currentPollingRequest.requestId,
        pointCost: 0,
        // 호환성 필드들
        id: `polling-${currentPollingRequest.requestId}-${index}`,
        url: '',
        prompt: currentPollingRequest.prompt,
        type: 'pending' as const,
      }))
    : []

  // 폴링 스켈레톤 + 실제 데이터 병합
  const allImages: ImageData[] = [...pollingSkeletons, ...realImages]

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="text-white">로딩 중...</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="text-red-400">데이터를 불러오는데 실패했습니다.</div>
      </div>
    )
  }

  return (
    <div className="relative h-screen w-screen bg-black">
      {/* 메인 콘텐츠 */}
      <div className="absolute inset-0">
        <div className="h-full w-full">
          {allImages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center text-zinc-400">
                <p className="mb-2 text-lg">아직 생성된 이미지가 없습니다</p>
                <p className="text-sm">
                  아래에서 새로운 이미지를 생성해보세요!
                </p>
              </div>
            </div>
          ) : (
            <div id="scrollable-div" className="h-full overflow-auto pb-80">
              <InfiniteScroll
                dataLength={allImages.length}
                next={fetchNextPage}
                hasMore={hasNextPage}
                loader={
                  <div className="py-4 text-center text-zinc-400">
                    {isFetchingNextPage ? '더 불러오는 중...' : '로딩 중...'}
                  </div>
                }
                scrollableTarget="scrollable-div"
                endMessage={
                  <div className="py-4 text-center text-zinc-500"></div>
                }
              >
                <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {allImages.map((image) => (
                    <ImageItem
                      key={`job-${image.jobId}`}
                      image={image}
                      onClick={
                        image.type === 'completed' && image.url
                          ? () => handleImageClick(image.url!)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </InfiniteScroll>
            </div>
          )}
        </div>
      </div>

      {/* 하단 고정 생성 바 */}
      <GenerationControlBar 
        onStartPolling={startPolling}
        onPollingComplete={onPollingComplete}
      />

      {/* 이미지 확대 모달 */}
      {selectedImageUrl && (
        <ImageSlideModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          images={[selectedImageUrl]}
          initialIndex={0}
          showCounter={false}
        />
      )}
    </div>
  )
}
